/**
 * Bank Deposit dialog — records cash that physically left the till and was
 * deposited into the company bank account. Under the hood this is just a
 * payment voucher with:
 *
 *   { kind: "payment", party_type: "self", category: "bank_deposit",
 *     method: "cash", party_name: "Bank Deposit" }
 *
 * The Cash Position report breaks these out separately from regular expense
 * payments so accountants can distinguish "cash that moved to bank" from
 * "cash actually spent".
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Drawer, { FormField, FormTextarea, getSaveLabel } from "@/components/Drawer";
import BranchSelect from "@/components/BranchSelect";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  branch_id: "",
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  reference: "",
  narration: "",
};

export default function BankDepositDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Branch operators must record deposits on the day they physically happen.
  // Only admins can backdate (e.g. correcting a missed entry from yesterday).
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (open) setForm(isAdmin ? EMPTY : { ...EMPTY, date: today });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!form.branch_id) {
      toast.error("Pick a branch.");
      return;
    }
    if (!amt || amt <= 0) {
      toast.error("Deposit amount must be greater than zero.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/vouchers", {
        kind: "payment",
        branch_id: form.branch_id,
        date: form.date,
        party_type: "self",
        party_name: "Bank Deposit",
        category: "bank_deposit",
        narration: form.narration || "Cash deposited to bank",
        lines: [{
          method: "cash",
          amount: amt,
          reference: (form.reference || "").trim(),
        }],
      });
      toast.success("Bank deposit recorded");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Cashbook"
      title="Bank Deposit"
      testIdClose="close-bank-deposit"
    >
      <form onSubmit={submit} data-testid="bank-deposit-form" className="space-y-4">
        <div className="border border-[#0EA5E9]/40 bg-[#0EA5E9]/5 p-3 text-[11px] text-ink">
          Records cash transferred from this branch&apos;s till to the bank. Counted as a payment
          (cash leaves the drawer) but reported separately in the Cash Position report so it
          is not mistaken for an expense.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={isAdmin ? "Date" : "Date (today)"}
            type="date"
            value={form.date}
            onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            testId="bank-deposit-date"
            required
            min={isAdmin ? undefined : today}
            max={isAdmin ? undefined : today}
          />
          <BranchSelect
            value={form.branch_id}
            onChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}
            testId="bank-deposit-branch"
            required
          />
        </div>

        <FormField
          label="Amount deposited (₹)"
          type="number"
          value={form.amount}
          onChange={(v) => setForm((f) => ({ ...f, amount: v }))}
          testId="bank-deposit-amount"
          mono
          required
        />

        <FormField
          label="Slip / UTR reference (optional)"
          value={form.reference}
          onChange={(v) => setForm((f) => ({ ...f, reference: v }))}
          testId="bank-deposit-reference"
          mono
        />

        <FormTextarea
          label="Narration"
          value={form.narration}
          onChange={(v) => setForm((f) => ({ ...f, narration: v }))}
          testId="bank-deposit-narration"
        />

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-bank-deposit"
            className="px-4 py-2.5 bg-[#0EA5E9] text-white text-sm font-semibold hover:bg-[#0284c7] transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, false, "Saving…", "", "Record deposit")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
